import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { collection, addDoc } from "firebase/firestore";
import { db } from "../../firebase"; 
import SubHero from '../other/SubHero';

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0rem 2rem;
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #13182a;
  padding: 2rem;
  border-radius: 4px;
`;

const InfoSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  font-family: 'Garamond', serif;
  color: #f0f0f0;
  margin-bottom: 1rem;
`;

const InfoText = styled.p`
  font-size: 1.125rem;
  font-family: 'Garamond', serif;
  line-height: 1.6;
  color: #f0f0f0;
  margin-bottom: 0.5rem;
`;

const ContactLink = styled.a`
  color: #f0f0f0;
  font-family: 'Garamond', serif;
  text-decoration: none;
  position: relative;

  &:hover {
    font-weight: 600;
    text-decoration: underline;
  }
`;

const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 1rem;
  font-weight: 500;
  font-family: 'Garamond', serif;
  color: #13182a;
  margin-bottom: 0.1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  font-size: 1rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  transition: border-color 0.3s ease;
  min-height: 50px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #000;
  }
`;

const SubmitButton = styled.button`
  background-color: #13182a;
  color: #f0f0f0;
  border: none;
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  font-family: 'Garamond', serif;
  cursor: pointer;
  transition: background-color 0.3s ease;
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    background-color: #303442;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  border: 2px solid #f3f3f3;
  border-top: 2px solid #000;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SuccessMessage = styled.div`
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  font-family: 'Garamond', serif;
  border-radius: 4px;
`;

const OfficeSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-top: 4rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageCanvas = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 2fr 1fr 1fr;
  grid-template-areas: 
    "leftBig rightSmall"
    "leftBig rightBig"
    "leftSmall rightBig";
  gap: 1rem;
  height: 600px;
`;

const OfficeImage = styled.img.attrs({
  loading: 'lazy'
})`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0px;
  box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.3);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }

  &:nth-child(1) {
    grid-area: leftBig;
  }

  &:nth-child(2) {
    grid-area: leftSmall;
  }

  &:nth-child(3) {
    grid-area: rightSmall;
  }

  &:nth-child(4) {
    grid-area: rightBig;
  }
`;

const OfficeInfo = styled.div`
  display: flex;
  flex-direction: column;
  // color of the background
  background-color: #13182a;
  padding: 2rem;
  border-radius: 4px;
`;

const OfficeTitle = styled.h3`
  font-size: 1.75rem;
  font-weight: 400;
  margin-bottom: 0.5rem;
  font-family: 'Garamond', serif;
  color: #f0f0f0;
`;

const OfficeUnderline = styled.div`
  width: 2rem;
  height: 2px;
  background-color: #f0f0f0;
  margin-bottom: 1.5rem;
`;

const OfficeText = styled.p`
  font-size: 1.125rem;
  line-height: 1.6;
  font-family: 'Garamond', sans-serif;
  color: #f0f0f0;
  margin-bottom: 1.5rem;
  text-align: justify;
`;

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      const firstInvalid = form.querySelector(':invalid') as HTMLElement;
      if (firstInvalid) {
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalid.focus();
      }
      return;
    }
  
    setIsLoading(true);
  
    try {
      const docRef = await addDoc(collection(db, "contactmessages"), {
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
        timestamp: new Date(),
      });
      // eslint-disable-next-line no-console
      console.log("Document written with ID:", docRef.id);
      setIsSubmitted(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error submitting form:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContactContainer>
      <SubHero title="Contact Us" />

      <Helmet>
        <title>Heaviside | Contact</title>
        <meta
          name="description"
          content="Get in touch with Heaviside, a next-generation quantitative hedge fund."
        />
        <meta
          name="keywords"
          content="Heaviside, contact, hedge fund, Dubai, Business Bay"
        />
        <meta name="author" content="Heaviside Team" />
        <meta property="og:title" content="Contact Heaviside – Quant Hedge Fund" />
        <meta property="og:description" content="Reach out to Heaviside, a technology-first quantitative hedge fund based in Dubai, UAE." />
        <meta property="og:url" content="https://hvside.com" />
        <meta property="og:image" content="https://hvside.com/logo_text_svg.svg" />
        <link rel="icon" href="/logo_white.ico" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Heaviside",
            "image": "https://hvside.com/logo_text_svg.svg",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1606-02, The Binary Tower by OMNIYAT",
              "addressLocality": "Business Bay",
              "addressRegion": "Dubai",
              "addressCountry": "UAE"
            },
            "email": "info@hvside.com",
            "url": "https://hvside.com/contact",
            "description": "Contact the Heaviside team, a next-generation quantitative hedge fund located in Dubai.",
            "contactPoint": {
              "@type": "ContactPoint",
              "email": "info@hvside.com",
              "contactType": "Customer Support"
            }
          }
          `}
        </script>
      </Helmet> 
      <ContactGrid>
        <ContactInfo>
          <InfoSection>
            <SectionTitle>General Inquiries</SectionTitle>
            <InfoText>
              <ContactLink href="mailto:info@hvside.com">
                info@hvside.com
              </ContactLink>
            </InfoText>
          </InfoSection>

          <InfoSection>
            <SectionTitle>Office Location</SectionTitle>
            <InfoText>1606-02 — The Binary Tower by OMNIYAT</InfoText>
            <InfoText>Business Bay, Dubai</InfoText>
            <InfoText>United Arab Emirates</InfoText>
          </InfoSection>
        </ContactInfo>

        <ContactForm onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              name="subject"
              type="text"
              value={formData.subject}
              onChange={handleChange}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="message">Message</Label>
            <TextArea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
            />
          </FormGroup>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? (
              <>
                <Spinner />
                Sending...
              </>
              ) : (
              "Send Message"
              )}
            </SubmitButton>
            {isSubmitted && (
              <SuccessMessage style={{ marginLeft: '1rem' }}>
              Message sent.
              </SuccessMessage>
            )}
            </div>
        </ContactForm>
      </ContactGrid>

      <OfficeSection>
        <ImageCanvas>
          <OfficeImage src="/assets/thebinary_out2.webp" alt="Exterior 1" />
          <OfficeImage src="/assets/thebinary_out3.webp" alt="Exterior 2" />
          <OfficeImage src="/assets/thebinary_out1.webp" alt="Exterior 3" />
          <OfficeImage src="/assets/thebinary_in1.webp" alt="Lobby" />
        </ImageCanvas>

        <OfficeInfo>
          <OfficeTitle>UAE Offices</OfficeTitle>
          <OfficeUnderline />
          <OfficeText>
            We are proud to present our first office at The Binary Tower by OMNIYAT, located in Business Bay, Dubai, a place we believe is the new land of innovation.
          </OfficeText>
        </OfficeInfo>
      </OfficeSection>
    </ContactContainer>
  );
};

export default ContactPage;